import React from 'react'
import { ScoutIcon } from 'components/icons/scout'
import { PaperPlaneIcon } from 'components/icons/paper-plane'
import { v4 as uuid } from 'uuid'
import { streamAnalyticsChatResponse, AnalyticsChatResponse } from './analytics-insights-data'
import { AnalyticsDataDisplay } from './analytics-data-display'
import '../template-library/_styles.scss'
import '../content-pool/suggested/_styles.scss'

interface AnalyticsChatMessage {
  role: 'user' | 'assistant'
  content: string
  timestamp?: Date
  data?: any
}

interface QuickAction {
  prompt: string
  category: string
}

interface AnalyticsInsightsChatProps {
  communityId: string
  onError: (error: string) => void
}

interface AnalyticsInsightsChatState {
  messages: AnalyticsChatMessage[]
  inputMessage: string
  isLoading: boolean
  isTyping: boolean
  sessionId: string
  showWelcome: boolean
}

export class AnalyticsInsightsChat extends React.Component<AnalyticsInsightsChatProps, AnalyticsInsightsChatState> {
  private chatEndRef: React.RefObject<HTMLDivElement>
  private chatInputRef: React.RefObject<HTMLInputElement>

  constructor(props: AnalyticsInsightsChatProps) {
    super(props)
    this.state = {
      messages: [],
      inputMessage: '',
      isLoading: false,
      isTyping: false,
      sessionId: uuid(),
      showWelcome: true
    }
    this.chatEndRef = React.createRef()
    this.chatInputRef = React.createRef()
  }

  componentDidMount() {
    this.scrollToBottom()
  }

  componentDidUpdate(prevProps: AnalyticsInsightsChatProps, prevState: AnalyticsInsightsChatState) {
    // Scroll when messages change (new messages or content updates during streaming)
    if (prevState.messages.length !== this.state.messages.length) {
      this.scrollToBottom()
    } else if (this.state.messages.length > 0 && prevState.messages.length > 0) {
      // Check if last message content changed (streaming updates)
      const lastMessage = this.state.messages[this.state.messages.length - 1]
      const prevLastMessage = prevState.messages[prevState.messages.length - 1]
      if (lastMessage.content !== prevLastMessage.content) {
        this.scrollToBottom()
      }
    }

    // Scroll when typing indicator changes
    if (prevState.isTyping !== this.state.isTyping) {
      this.scrollToBottom()
    }
  }

  private scrollToBottom = () => {
    requestAnimationFrame(() => {
      setTimeout(() => {
        if (this.chatEndRef && this.chatEndRef.current) {
          this.chatEndRef.current.scrollTop = this.chatEndRef.current.scrollHeight
        }
      }, 50)
    })
  }

  private handleSendMessage = () => {
    const message = this.state.inputMessage.trim()
    if (!message || this.state.isLoading) return

    const userMessage: AnalyticsChatMessage = {
      role: 'user',
      content: message,
      timestamp: new Date()
    }

    this.setState(
      {
        messages: [...this.state.messages, userMessage],
        inputMessage: '',
        isLoading: true,
        isTyping: true,
        showWelcome: false
      },
      () => {
        this.sendToBackend(message)
      }
    )
  }

  private sendToBackend = (message: string) => {
    const payload = {
      message,
      sessionId: this.state.sessionId,
      chatType: 'analytics-insights',
      availableIndexes: ['email-events', 'message-run-details', 'topic-preference-actions'],
      defaultDateRange: 'last_30_days'
    }

    let assistantMessage = ''
    let receivedAnyText = false

    const callback = (response: AnalyticsChatResponse | null) => {
      if (!response) {
        this.setState({
          isLoading: false,
          isTyping: false
        })
        return
      }

      if (response.error) {
        this.setState({
          isLoading: false,
          isTyping: false
        })
        this.props.onError(response.error || 'Failed to communicate with Scout. Please try again.')
        return
      }

      if (response.message) {
        receivedAnyText = true
        assistantMessage += response.message
        this.setState((prevState) => {
          const messages = [...prevState.messages]
          const lastMessage = messages[messages.length - 1]

          if (lastMessage && lastMessage.role === 'assistant') {
            messages[messages.length - 1] = {
              ...lastMessage,
              content: assistantMessage
            }
          } else {
            messages.push({
              role: 'assistant',
              content: assistantMessage,
              timestamp: new Date()
            })
          }

          return { messages, isTyping: false }
        })
      }

      if (response.done) {
        this.setState({
          isLoading: false,
          isTyping: false
        })

        if (!receivedAnyText) {
          this.setState((prevState) => {
            const messages = [...prevState.messages]
            messages.push({
              role: 'assistant',
              content: 'Query completed. Let me know if you need any other analytics insights!',
              timestamp: new Date()
            })
            return { messages }
          })
        }
      }
    }

    streamAnalyticsChatResponse(this.props.communityId, payload, callback)
  }

  private handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      this.handleSendMessage()
    }
  }

  private handleSuggestedQuery = (query: string) => {
    if (this.state.isLoading) return

    this.setState({ inputMessage: query }, () => {
      this.handleSendMessage()
    })
  }

  private renderWelcome = () => {
    const quickActions: QuickAction[] = [
      {
        prompt: 'Show me top 10 clicked domains last month',
        category: 'Email Performance'
      },
      {
        prompt: 'What are my click trends over the last 90 days?',
        category: 'Email Performance'
      },
      {
        prompt: 'Which topics are most popular with subscribers?',
        category: 'Content Analysis'
      },
      {
        prompt: 'Show me bounce rate trends over time',
        category: 'Email Performance'
      },
      {
        prompt: 'What email clients have the highest open rates?',
        category: 'Email Performance'
      },
      {
        prompt: 'Which subscribers are most engaged with my content?',
        category: 'Subscriber Behavior'
      }
    ]

    return (
      <div className="rex-welcome-section">
        <div className="rex-welcome-message">
          <ScoutIcon svgwidth="40" svgheight="40"/>
          <div>
            <h2>Hi there! I'm Scout</h2>
            <p>
              I can help you analyze your email performance, content engagement, and subscriber behavior.
              Ask me questions in natural language and I'll provide insights and custom reports from your analytics data.
            </p>
          </div>
        </div>

        <div className="rex-quick-actions">
          <h3>Try asking me...</h3>
          <div className="rex-action-grid">
            {quickActions.map((action, index) => (
              <button
                key={index}
                className="rex-action-btn"
                onClick={() => {
                  this.setState({ inputMessage: action.prompt }, () => {
                    this.handleSendMessage()
                  })
                }}
              >
                <div style={{ fontSize: '0.75em', color: '#666', marginBottom: '4px' }}>
                  {action.category}
                </div>
                {action.prompt}
              </button>
            ))}
          </div>
        </div>
      </div>
    )
  }

  private renderMessage = (message: AnalyticsChatMessage, index: number) => {
    return (
      <div
        key={index}
        className={`rex-message ${message.role === 'user' ? 'user' : 'assistant'}`}
      >
        <div className="rex-message-avatar">
          {message.role === 'user' ? (
            <i className="fas fa-user" />
          ) : (
            <ScoutIcon svgwidth="24" svgheight="24" />
          )}
        </div>
        <div className="rex-message-content">
          <AnalyticsDataDisplay
            content={message.content}
            onSuggestedQuery={message.role === 'assistant' ? this.handleSuggestedQuery : undefined}
          />
          {message.timestamp && (
            <div className="rex-message-timestamp">
              {message.timestamp.toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
              })}
            </div>
          )}
        </div>
      </div>
    )
  }

  private renderTypingIndicator = () => {
    return (
      <div className="rex-message assistant">
        <div className="rex-message-avatar">
          <ScoutIcon svgwidth="24" svgheight="24" />
        </div>
        <div className="rex-message-content">
          <div className="rex-typing-indicator">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      </div>
    )
  }

  render() {
    return (
      <div className="rex-main-content">
        <div className="rex-chat-container" ref={this.chatEndRef}>
          <div className="rex-messages">
            {this.state.showWelcome && this.state.messages.length === 0 && this.renderWelcome()}

            {this.state.messages.map((message, index) => this.renderMessage(message, index))}

            {this.state.isTyping && this.renderTypingIndicator()}
          </div>
        </div>

        <div className="rex-input-container">
          <form className="rex-input-form" onSubmit={(e) => { e.preventDefault(); this.handleSendMessage(); }}>
            <div className="rex-input-wrapper">
              <input
                ref={this.chatInputRef}
                type="text"
                className="rex-message-input"
                placeholder="Ask Scout about your analytics data..."
                value={this.state.inputMessage}
                onChange={(e) => this.setState({ inputMessage: e.target.value })}
                onKeyDown={this.handleKeyDown}
                disabled={this.state.isLoading}
              />
              <button
                type="submit"
                className="rex-send-btn"
                disabled={!this.state.inputMessage.trim() || this.state.isLoading}
              >
                <PaperPlaneIcon />
              </button>
            </div>
          </form>
          <div className="rex-input-footer">
            <small>Scout is powered by AI and may make mistakes. Please verify important data.</small>
          </div>
        </div>
      </div>
    )
  }
}
